import { doc, writeBatch, Timestamp, getDoc, type DocumentData } from 'firebase/firestore';
import { db } from '../../config';
import { createResultHistory } from './history';
import { ResultHistoryActionType } from '../../../../types/resultHistory';

/** Firestore バッチ書き込み上限 */
const FIRESTORE_BATCH_LIMIT = 500;

export interface BulkUpdateProcessingStatusRequest {
  resultIds: string[];
  /** 変更後の処理ステータス（processingStatus の値） */
  processingStatus: number;
  updatedBy: string;
  updatedByName: string;
}

export interface BulkUpdateProcessingStatusResponse {
  /** 更新できた件数 */
  updatedCount: number;
  /** 更新できなかった件数 */
  failedCount: number;
}

/**
 * result レコードの処理ステータスを一括変更する（#3262）。
 *
 * 「要注意以外を確認済にする」から呼ぶ。
 * 500件超は内部でバッチ分割する。
 *
 * 途中で失敗しても成功分はロールバックしない（設計書 §8.4）。
 * 全件ロールバックすると、大量件数のときに1件の失敗で全部やり直しになり
 * かえって使いづらいため、成功・失敗の件数を返して呼び出し側で伝える。
 *
 * 誰がいつ変更したかは result_history に残す。ユーザー自身の操作である
 * ことを後から確認できるようにするため（設計書 §8.4 の担保その1）。
 */
export const bulkUpdateProcessingStatus = async ({
  resultIds,
  processingStatus,
  updatedBy,
  updatedByName,
}: BulkUpdateProcessingStatusRequest): Promise<BulkUpdateProcessingStatusResponse> => {
  if (resultIds.length === 0) {
    return { updatedCount: 0, failedCount: 0 };
  }

  const now = Timestamp.now();
  const updateData = {
    processing_status: processingStatus,
    updated_at: now,
    updated_by: updatedBy,
  };

  // 履歴記録用に変更前の値を取得
  const previousSnapshots = await Promise.all(
    resultIds.map(async id => {
      try {
        const snap = await getDoc(doc(db, 'results', id));
        return { id, data: snap.exists() ? (snap.data() as DocumentData) : null };
      } catch {
        return { id, data: null };
      }
    }),
  );

  // バッチ書き込み（500件ずつ分割）。
  // 1バッチが失敗しても残りのバッチは続行する
  let updatedCount = 0;
  let failedCount = 0;
  const succeededIds: string[] = [];

  for (let i = 0; i < resultIds.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = resultIds.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach(resultId => {
      batch.update(doc(db, 'results', resultId), updateData);
    });
    try {
      await batch.commit();
      updatedCount += chunk.length;
      succeededIds.push(...chunk);
    } catch {
      failedCount += chunk.length;
    }
  }

  // 履歴記録（失敗してもメイン処理は止めない）。実際に更新できた分のみ残す
  const succeededIdSet = new Set(succeededIds);
  await Promise.all(
    previousSnapshots.map(async ({ id, data }) => {
      if (!data || !succeededIdSet.has(id)) return;
      try {
        await createResultHistory({
          result_id: id,
          action_type: ResultHistoryActionType.EDIT,
          changed_fields: { processing_status: processingStatus },
          previous_values: { processing_status: data.processing_status ?? null },
          user_id: updatedBy,
          user_name: updatedByName,
        });
      } catch {
        // 履歴記録の失敗はメイン処理を止めない
      }
    }),
  );

  return { updatedCount, failedCount };
};
